import { fetchProducts, fetchProductCategories } from './inventoryApi.js';
import { fetchReceiptSettings, updateReceiptSettings } from './branchApi.js';
import { createWalkinOrder, createTableOrder, createWaiterOrder } from './orderApi.js';
import { getWaiters } from './waiterApi.js';

const DB_NAME = 'iceCreamBranchOfflineDB';
const DB_VERSION = 1;
const STORE_CONFIG = {
  branch_settings: { keyPath: 'key' },
  product_categories: { keyPath: 'id' },
  products: { keyPath: 'id' },
  orders: { keyPath: 'id' },
  token: { keyPath: 'id' },
  receipt: { keyPath: 'id' },
  waiters: { keyPath: 'id' }
};

const requestToPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });

const openDb = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      Object.entries(STORE_CONFIG).forEach(([name, config]) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, config);
        }
      });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open IndexedDB'));
  });

const waitForTransactionComplete = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  });

const withStore = async (storeName, mode, callback) => {
  const db = await openDb();
  const tx = db.transaction(storeName, mode);
  const store = tx.objectStore(storeName);
  try {
    const result = await callback(store);
    await waitForTransactionComplete(tx);
    return result;
  } finally {
    db.close();
  }
};

const requestPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });

const getDbStore = async (storeName, mode = 'readonly') => {
  const db = await openDb();
  const tx = db.transaction(storeName, mode);
  return { db, tx, store: tx.objectStore(storeName) };
};

const getAllFromStore = async (storeName) => {
  const { db, tx, store } = await getDbStore(storeName, 'readonly');
  const request = store.getAll();
  const result = await requestPromise(request);
  await waitForTransactionComplete(tx);
  db.close();
  return result;
};

const getFromStore = async (storeName, key) => {
  const { db, tx, store } = await getDbStore(storeName, 'readonly');
  const request = store.get(key);
  const result = await requestPromise(request);
  await waitForTransactionComplete(tx);
  db.close();
  return result;
};

const putInStore = async (storeName, item) => {
  const { db, tx, store } = await getDbStore(storeName, 'readwrite');
  const request = store.put(item);
  const result = await requestPromise(request);
  await waitForTransactionComplete(tx);
  db.close();
  return result;
};

const deleteDb = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error || new Error('Unable to delete IndexedDB'));
    request.onblocked = () => reject(new Error('Database deletion blocked'));
  });

const ensureDb = async () => openDb();

const clearStore = async (storeName) => {
  const { db, tx, store } = await getDbStore(storeName, 'readwrite');
  const request = store.clear();
  const result = await requestPromise(request);
  await waitForTransactionComplete(tx);
  db.close();
  return result;
};

const initializeOfflineDb = async (branchId) => {
  if (!navigator.onLine) {
    throw new Error('Online connection required to initialize offline mode.');
  }

  await deleteDb();
  await ensureDb();

  const productResponse = await fetchProducts();
  const categoryResponse = await fetchProductCategories();
  const branchData = await fetchReceiptSettings(branchId);
  const waitersResponse = await getWaiters();

  const categories = categoryResponse.categories || [];
  const products = productResponse.products || [];
  const waiters = waitersResponse.waiters || [];

  const receiptSettings = branchData.receiptSettings || [];
  const branchSettings = branchData.branchSettings || {};

  const lastReceiptNo = Number(branchData.lastReceiptNumber || 0) || 0;
  const lastOrderNo = Number(branchData.lastOrderNumber || 0) || 0;
  const initialTokenNumber = Number(branchSettings.token_number || 1) || 1;
  const offlineModeFlag = true;

  await Promise.all([
    ...categories.map((category) => putInStore('product_categories', { id: category.id || category.name || category.value || `cat-${Math.random()}`, name: category.name || category.value || category.label || '' })),
    ...products.map((product) => putInStore('products', { ...product, id: product.id || product.product_id || `prod-${Math.random()}` })),
    ...waiters.map((waiter) => putInStore('waiters', { id: waiter.id || waiter.user_id || `waiter-${Math.random()}`, name: waiter.name || waiter.full_name || '', phone: waiter.phone || '', user_id: waiter.user_id || waiter.id })),
    putInStore('branch_settings', { key: 'offline_mode', value: offlineModeFlag ? '1' : '0' }),
    putInStore('branch_settings', { key: 'token_number', value: String(initialTokenNumber) }),
    putInStore('branch_settings', { key: 'order_number', value: String(lastOrderNo + 1) }),
    putInStore('branch_settings', { key: 'receipt_number', value: String(lastReceiptNo + 1) }),
    putInStore('token', { id: 'current_token', token_number: initialTokenNumber }),
    putInStore('receipt', { id: 'last_receipt', receipt_no: String(lastReceiptNo), branch_id: branchId, updated_at: new Date().toISOString() })
  ]);

  return true;
};

const getOfflineBranchSettings = async () => {
  const settings = await getAllFromStore('branch_settings');
  return settings.reduce((acc, item) => {
    acc[item.key] = item.value;
    return acc;
  }, {});
};

const setOfflineBranchSetting = async (key, value) => {
  await putInStore('branch_settings', { key, value: String(value) });
};

const getOfflineProducts = async () => {
  return await getAllFromStore('products');
};

const getOfflineCategories = async () => {
  return await getAllFromStore('product_categories');
};

const getOfflineWaiters = async () => {
  return await getAllFromStore('waiters');
};

const getOfflineOrders = async () => {
  return await getAllFromStore('orders');
};

const createOfflineOrder = async (orderData) => {
  await ensureDb();
  const offlineSettings = await getOfflineBranchSettings();
  const nextOrderNumber = Number(offlineSettings.order_number || 1);
  const nextTokenNumber = Number(offlineSettings.token_number || 1);
  const nextReceiptNumber = Number(offlineSettings.receipt_number || 1);

  const now = new Date().toISOString();
  const localOrderId = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

  const order = {
    id: localOrderId,
    order_number: String(nextOrderNumber).padStart(5, '0'),
    order_type: orderData.order_type || 'walkin',
    branch_id: orderData.branch_id,
    user_id: orderData.user_id,
    waiter_id: orderData.waiter_id || null,
    table_id: orderData.table_id || null,
    subtotal: Number(orderData.subtotal || 0),
    discount: Number(orderData.discount || 0),
    total: Number(orderData.total || 0),
    amount_paid: Number(orderData.amount_paid || 0),
    change_given: Number(orderData.change_given || 0),
    payment_method: orderData.payment_method || 'cash',
    notes: orderData.notes || null,
    action: orderData.action || 'save',
    items: (orderData.items || []).map((item, index) => ({
      id: item.id || `item-${index}-${Math.random().toString(36).substr(2, 4)}`,
      product_id: item.product_id,
      product_variant_id: item.product_variant_id || null,
      quantity: Number(item.quantity || 0),
      unit_price: Number(item.unit_price || item.price || 0),
      name: item.name || ''
    })),
    token_number: orderData.order_type === 'table' ? null : nextTokenNumber,
    receipt_number: String(nextReceiptNumber).padStart(5, '0'),
    status: 'pending',
    synced: false,
    created_at: now,
    updated_at: now
  };

  await putInStore('orders', order);
  await setOfflineBranchSetting('order_number', String(nextOrderNumber + 1));
  await setOfflineBranchSetting('token_number', String(nextTokenNumber + 1));
  await setOfflineBranchSetting('receipt_number', String(nextReceiptNumber + 1));

  await putInStore('token', { id: 'current_token', token_number: nextTokenNumber + 1 });
  await putInStore('receipt', { id: 'last_receipt', receipt_no: String(nextReceiptNumber), branch_id: order.branch_id, updated_at: now });

  return order;
};

const appendOfflineOrderItems = async (orderId, items, action = 'save') => {
  const order = await getFromStore('orders', orderId);
  if (!order) {
    throw new Error('Offline order not found');
  }

  const updatedItems = [
    ...order.items,
    ...(items || []).map((item, index) => ({
      id: item.id || `item-${Date.now()}-${index}`,
      product_id: item.product_id,
      product_variant_id: item.product_variant_id || null,
      quantity: Number(item.quantity || 0),
      unit_price: Number(item.unit_price || item.price || 0),
      name: item.name || ''
    }))
  ];

  const updatedOrder = {
    ...order,
    items: updatedItems,
    action,
    total: Number(order.total || 0) + updatedItems.reduce((sum, item) => sum + (Number(item.unit_price || 0) * Number(item.quantity || 0)), 0),
    updated_at: new Date().toISOString(),
    synced: false
  };

  await putInStore('orders', updatedOrder);
  return updatedOrder;
};

const cancelOfflineOrder = async (orderId) => {
  const order = await getFromStore('orders', orderId);
  if (!order) {
    throw new Error('Offline order not found');
  }
  const cancelledOrder = { ...order, status: 'cancelled', updated_at: new Date().toISOString(), synced: false };
  await putInStore('orders', cancelledOrder);
  return cancelledOrder;
};

const syncOfflineOrdersToServer = async (branchId) => {
  if (!navigator.onLine) {
    throw new Error('Cannot sync while offline. Connect to the internet and try again.');
  }

  const offlineOrders = await getOfflineOrders();
  const unsynced = (offlineOrders || []).filter((order) => !order.synced && order.status !== 'cancelled');

  const results = [];
  for (const order of unsynced) {
    let response;
    const payload = {
      action: order.action || 'save',
      branch_id: order.branch_id,
      user_id: order.user_id,
      order_type: order.order_type,
      table_id: order.table_id,
      waiter_id: order.waiter_id,
      subtotal: order.subtotal,
      tax_amount: 0,
      discount: order.discount,
      total: order.total,
      amount_paid: order.amount_paid,
      change_given: order.change_given,
      paid_at: order.amount_paid > 0 ? new Date().toISOString() : null,
      payment_method: order.payment_method,
      notes: order.notes,
      items: order.items.map((item) => ({
        product_id: item.product_id,
        product_variant_id: item.product_variant_id,
        quantity: item.quantity,
        unit_price: item.unit_price
      }))
    };

    try {
      if (order.order_type === 'table') {
        response = await createTableOrder(payload);
      } else if (order.order_type === 'waiter') {
        response = await createWaiterOrder(payload);
      } else {
        response = await createWalkinOrder(payload);
      }
    } catch (err) {
      throw new Error(`Failed to sync offline order ${order.order_number || order.id}: ${err.message}`);
    }

    const syncedOrder = {
      ...order,
      synced: true,
      remote_order_id: response.order?.id || response.order?.order_id || null,
      remote_order_number: response.order?.order_number || response.order?.order_number || order.order_number,
      updated_at: new Date().toISOString()
    };
    await putInStore('orders', syncedOrder);
    results.push(syncedOrder);
  }

  return results;
};

const loadOfflineBranchReceiptData = async () => {
  const settings = await getOfflineBranchSettings();
  return {
    shopName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    taxId: '',
    currency: 'PKR',
    footerMessage: '',
    showTaxOnReceipt: true,
    taxPercentage: 8.5,
    returnPolicy: '',
    enableAutoDiscount: false,
    discountType: 'fixed',
    discountValue: 0,
    shiftStartHour: Number(settings.shift_start_hour || 15),
    tokenNumber: Number(settings.token_number || 1),
    sendTokenToStaff: settings.send_token_to_staff === '0' ? false : true,
    offlineMode: settings.offline_mode === '1',
  };
};

const readOfflineFlag = async () => {
  const settings = await getOfflineBranchSettings();
  return settings.offline_mode === '1';
};

export {
  initializeOfflineDb,
  deleteDb,
  getOfflineProducts,
  getOfflineCategories,
  getOfflineWaiters,
  getOfflineOrders,
  getOfflineBranchSettings,
  setOfflineBranchSetting,
  createOfflineOrder,
  appendOfflineOrderItems,
  cancelOfflineOrder,
  syncOfflineOrdersToServer,
  loadOfflineBranchReceiptData,
  readOfflineFlag,
};
